<template>
  <div class="min-h-screen p-6 flex flex-col lg:flex-row gap-6">
    <!-- Left Column -->
    <div
      class="bg-white p-6 rounded-lg shadow-lg w-full sm:overflow-hidden space-y-4 lg:flex justify-between"
    >
      <div class="px-2 sm:px-4 md:px-6 lg:px-6 w-full lg:w-1/2 space-y-4">
        <div
          class="relative w-full pb-[56.25%] h-0 overflow-hidden rounded-lg shadow-md mb-6"
        >
          <iframe
            class="absolute top-0 left-0 w-full h-full"
            src="https://www.youtube.com/embed/Wyy-XMvZYF4?start=14"
            title="Mshel Foundation Video"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
        <p
          class="text-gray-700 text-base leading-relaxed text-justify pb-4 pt-4"
        >
          Our activities are guided by the UN's Sustainable Development Goals 1
          (No Poverty) and 4 (Quality Education). The Mshel Foundation is the
          philanthropic arm of Mshel Group, dedicated to giving back to the
          community through impactful initiatives.
        </p>
        <p class="text-gray-700 text-base leading-relaxed mt-4 text-justify">
          Dr. Barka Umaru Mshelia leads the Foundation, which aims to create
          transformative change, uplift communities, and catalyse sustainable
          development in Nigeria and beyond. The foundation was born from Dr.
          Barka Mshelia’s passion for helping the needy and empowering young
          entrepreneurs with mentorship and crucial financial support to help
          them turn their innovative ideas into thriving businesses.
        </p>
      </div>
      <!-- Right Column -->
      <div class="w-full px-2 max-w-full sm:max-w-xl mx-auto space-y-4 lg:pb-6">
        <div class="space-y-4 top-0">
          <div
            v-for="(item, index) in accordionItems"
            :key="index"
            class="border rounded-sm bg-gray-100 transition-all duration-300"
          >
            <div
              class="flex justify-between items-center p-4 cursor-pointer text-blue-600 font-semibold text-sm sm:text-base lg:text-lg"
              @click="toggleAccordion(index)"
            >
              <div class="flex-1 pr-2">
                <!-- Title should take full available space -->
                {{ item.title }}
              </div>
              <span
                v-if="item.title !== 'Mission'"
                class="transition-transform text-white bg-blue-700 rounded-full w-5 h-5 flex items-center justify-center text-xs sm:text-sm"
                :class="{ 'rotate-45': activeAccordion === index }"
              >
                +
              </span>
            </div>

            <transition name="accordion">
              <div
                v-show="activeAccordion === index"
                class="px-4 pb-4 text-gray-700 overflow-hidden text-sm sm:text-base"
              >
                <ul
                  v-if="Array.isArray(item.content)"
                  class="list-disc list-inside"
                >
                  <li v-for="(val, i) in item.content" :key="i">{{ val }}</li>
                </ul>
                <p v-else>{{ item.content }}</p>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- OUR TEAM Section -->
  <section class="py-24 bg-white relative overflow-hidden bg-pattern">
    <div class="container mx-auto px-6 relative z-10">
      <!-- Enhanced Section Header -->
      <div class="text-center mb-20">
        <div class="inline-block relative">
          <!-- Decorative Icons -->
          <i
            class="fas fa-users absolute -top-8 -left-8 text-yellow text-2xl floating-icon"
          ></i>
          <i
            class="fas fa-star absolute -top-6 -right-6 text-yellow text-lg floating-icon"
          ></i>

          <h2 class="text-5xl md:text-6xl font-black mb-6">
            Our <span class="text-shimmer">Team</span>
          </h2>
        </div>
      </div>

      <!-- Enhanced Team Grid -->
      <div class="max-w-7xl mx-auto">
        <!-- First Row - 3 Members -->
        <div class="flex flex-wrap justify-center gap-12 mb-16">
          <div
            v-for="member in firstRowMembers"
            :key="member.id"
            class="team-card w-80 rounded-3xl p-10 group relative one-side-rounded"
          >
            <!-- Background Pattern -->
            <div
              class="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-orange-100/30 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            ></div>

            <div class="relative z-10">
              <!-- Enhanced Profile Image -->
              <div class="w-36 h-36 mx-auto mb-8 relative">
                <div class="profile-ring w-full h-full">
                  <img
                    :src="member.image"
                    :alt="member.name"
                    class="w-full h-full object-cover rounded-full"
                  />
                </div>
              </div>

              <!-- Enhanced Member Info -->
              <div class="text-center space-y-4">
                <h3
                  class="text-lg font-bold text-gray-800 transition-colors duration-300"
                >
                  {{ member.name }}
                </h3>
                <div
                  class="position-badge px-6 py-3 rounded-full text-sm font-semibold inline-block"
                >
                  {{ member.position }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Second Row - 2 Members -->
        <div class="flex flex-wrap justify-center gap-12">
          <div
            v-for="member in secondRowMembers"
            :key="member.id"
            class="team-card w-80 rounded-3xl p-10 group relative one-side-rounded"
          >
            <div
              class="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-orange-100/30 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            ></div>

            <div class="relative z-10">
              <div class="w-36 h-36 mx-auto mb-8 relative">
                <div class="profile-ring w-full h-full">
                  <img
                    :src="member.image"
                    :alt="member.name"
                    class="w-full h-full object-cover rounded-full"
                  />
                </div>
              </div>

              <div class="text-center space-y-4">
                <h3
                  class="text-2xl font-bold text-gray-800 group-hover:text-blue-600 transition-colors duration-300"
                >
                  {{ member.name }}
                </h3>
                <div
                  class="position-badge px-6 py-3 rounded-full text-sm font-semibold inline-block"
                >
                  {{ member.position }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "TeamSection",

  data() {
    return {
      activeAccordion: 0,
      accordionItems: [
        {
          title: "Mission",
          content:
            "To empower individuals and communities through sustainable initiatives that promote education, health, and economic development.",
        },
        {
          title: "Vision",
          content:
            "To become a force in eradicating poverty and advancing quality education in underserved communities.",
        },
        {
          title: "Core Values",
          content: [
            "Integrity",
            "Empathy",
            "Collaboration",
            "Innovation",
            "Sustainability",
          ],
        },
        {
          title: "Main Goals",
          content:
            "We focus on community-driven solutions, leveraging local knowledge and resources to create lasting impact.",
        },
        {
          title: "Focus Areas",
          content: [
            "Education",
            "Health",
            "Empowerment",
            "Technology",
            "Economic Development",
          ],
        },
        {
          title: "How the Foundation Works",
          content:
            "We identify community needs, partner with stakeholders, and implement programs tailored to empower lives sustainably.",
        },
        {
          title: "Grantmaking",
          content:
            "Grants are awarded based on impact, sustainability, and alignment with our mission.",
        },
        {
          title: "Partnership and Collaboration",
          content:
            "We work with governments, NGOs, and private organizations to scale impact and drive community transformation.",
        },
      ],

      // Team members
      teamMembers: [
        {
          id: 1,
          name: "Dr. Musa Tarimbuka",
          position: "Chairman",
          image: require("../assets/images/MR MUS.png"),
        },
        {
          id: 2,
          name: "ARC. Dr. Barka Umaru Mshelia",
          position: "Founder",
          image: require("../assets/images/barkaimagee.3da92458.png"),
        },
        {
          id: 3,
          name: "Mrs. Lisa B. Mshelia",
          position: "Program Director",
          image: require("../assets/images/MRS LISA MSHELIA.871d07ae.png"),
        },
      ],
    };
  },

  computed: {
    firstRowMembers() {
      return this.teamMembers.slice(0, 3);
    },
    secondRowMembers() {
      return this.teamMembers.slice(3, 5);
    },
  },

  methods: {
    toggleAccordion(index) {
      this.activeAccordion = this.activeAccordion === index ? null : index;
    },
  },
};
</script>

<style scoped>
.team-card {
  @apply bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-100;
}

/* Accordion */

.accordion-leave-active {
  transition: max-height 1s ease, opacity 1s ease;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 500px;
  opacity: 1;
}

/* Card style */

.team-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(59, 130, 246, 0.05) 0%,
    rgba(147, 51, 234, 0.05) 100%
  );
  border-radius: 1rem;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.team-card:hover::before {
  opacity: 1;
}

.one-side-rounded {
  border-left: 6px solid #e89e1c;
  border-top-left-radius: -4rem;
  border-bottom-left-radius: 6rem;
}

@keyframes blob {
  0% {
    transform: translate(0px, 0px) scale(1);
  }
  33% {
    transform: translate(30px, -50px) scale(1.1);
  }
  66% {
    transform: translate(-20px, 20px) scale(0.9);
  }
  100% {
    transform: translate(0px, 0px) scale(1);
  }
}

.animate-blob {
  animation: blob 7s infinite;
}

.animation-delay-2000 {
  animation-delay: 2s;
}

/* Responsive adjustments */
@media (max-width: 1280px) {
  .team-card {
    @apply p-6;
  }
}

@media (max-width: 768px) {
  .team-card {
    @apply p-6;
  }
}
</style>
